import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  TextField,
  CircularProgress,
  Stack,
  Link,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { sendForgotPasswordEmail } from "@/api/user.service";
import AuthCard from "@/components/auth/AuthCard";
import { useNotification } from "@/context/NotificationContext";

type AuthMode = "login" | "forgot";

export const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<AuthMode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { notify } = useNotification();

  const handleAsync = async (callback: () => Promise<void>) => {
    setLoading(true);
    try {
      await callback();
    } catch (err) {
      if (err instanceof Error) {
        notify(err.message, "error");
      } else {
        notify("Erro inesperado. Tente novamente.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e: { preventDefault(): void }) => {
    e.preventDefault();

    if (!validateLogin()) return;

    handleAsync(async () => {
      try {
        await login(email, password);
        navigate("/dashboard", { replace: true });
      } catch (err) {
        if (err instanceof Error) {
          notify(err.message, "warning");
        } else {
          notify("Erro inesperado", "error");
        }
      }
    });
  };

  const validateLogin = () => {
    if (!email) {
      notify("O e-mail é obrigatório.", "info");
      return false;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      notify("Informe um e-mail válido.", "info");
      return false;
    }

    if (mode === "login" && !password) {
      notify("A senha é obrigatória.", "info");
      return false;
    }

    return true;
  };

  const handleForgetPass = () =>
    handleAsync(async () => {
      await sendForgotPasswordEmail(email);
      notify("Caso este email esteja cadastrado no sistema, um email de recuperação foi enviado.")
    });

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Left branding panel */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: '45%',
          background: (theme) =>
            `linear-gradient(155deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${theme.palette.primary.light} 100%)`,
          p: 6,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative circles */}
        <Box sx={{
          position: 'absolute', top: -80, right: -80,
          width: 320, height: 320, borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
        }} />
        <Box sx={{
          position: 'absolute', bottom: 60, left: -60,
          width: 240, height: 240, borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
        }} />
        <Box sx={{
          position: 'absolute', bottom: -40, right: 80,
          width: 160, height: 160, borderRadius: '50%',
          background: (theme) => alpha(theme.palette.primary.light, 0.18),
        }} />

        {/* Logo / brand name */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h4"
            sx={{ color: '#fff', fontWeight: 800, letterSpacing: '-0.5px' }}
          >
            App do Dono
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.7rem' }}
          >
            Painel de Gestão
          </Typography>
        </Box>

        {/* Center tagline */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.25, mb: 2 }}>
            As informações do seu negócio em um só lugar.
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.7 }}>
            Tenha uma visão completa e inteligente para tomar decisões com mais segurança.
          </Typography>
        </Box>

        {/* Bottom credit */}
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', position: 'relative', zIndex: 1 }}>
          © {new Date().getFullYear()} Davinti Tecnologia
        </Typography>
      </Box>

      {/* Right form panel */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: { xs: 3, sm: 6 },
        }}
      >
        {/* Mobile-only brand header */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 4, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="primary">
            App do Dono
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Painel de Gestão
          </Typography>
        </Box>

        <AuthCard>
          <Typography variant="h5" fontWeight={700} mb={0.5} color="text.primary">
            {mode === "login" ? "Bem-vindo de volta" : "Recuperar senha"}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {mode === "login"
              ? "Acesse o painel com suas credenciais."
              : "Informe seu e-mail para receber o link de recuperação."}
          </Typography>

          <Stack
            spacing={2}
            component="form"
            noValidate
            onSubmit={mode === "login" ? handleLogin : undefined}
          >
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            {mode === "login" && (
              <TextField
                label="Senha"
                type="password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}

            <Button
              type={mode === "login" ? "submit" : "button"}
              variant="contained"
              size="large"
              disabled={loading}
              onClick={mode === "forgot" ? handleForgetPass : undefined}
              sx={{ mt: 1, py: 1.5 }}
            >
              {loading
                ? <CircularProgress size={22} sx={{ color: 'primary.contrastText' }} />
                : mode === "login" ? "Entrar" : "Enviar"}
            </Button>

            <Link
              component="button"
              type="button"
              onClick={() => setMode(mode === "login" ? "forgot" : "login")}
              sx={{ alignSelf: "center", typography: "body2" }}
              underline="hover"
            >
              {mode === "login" ? "Esqueci a senha" : "Voltar para o login"}
            </Link>
          </Stack>
        </AuthCard>
      </Box>
    </Box>
  );
};
